import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
} from '@angular/core';

/**
 * Directive to inject custom styles into goab-dropdown shadow DOM.
 *
 * Usage:
 * 1. Apply to individual dropdowns:
 *    <goab-dropdown dropdownShadowStyle>...</goab-dropdown>
 *
 * 2. Apply with custom styles:
 *    <goab-dropdown [dropdownShadowStyle]="customCss">...</goab-dropdown>
 *
 * Default styles force dropdown icons to be visible (fixes shadow DOM visibility issues).
 */
@Directive({
  selector: '[dropdownShadowStyle]',
  standalone: true,
})
export class DropdownShadowStyleDirective implements AfterViewInit, OnDestroy {
  private static readonly STYLE_ID = 'dropdown-shadow-custom-styles';
  private mutationObserver?: MutationObserver;

  /**
   * Custom CSS to inject. If not provided, uses default icon visibility styles.
   */
  @Input() dropdownShadowStyle?: string;

  private readonly defaultStyles = `
    goa-icon,
    svg,
    .chevron,
    .dropdown-icon,
    [class*='icon'],
    [class*='chevron'] {
      display: inline-flex !important;
      visibility: visible !important;
      opacity: 1 !important;   
      color: #333 !important;
      fill: #333 !important; 
    }
  `;

  private readonly iconStyles = `
    svg,
    path,
    [class*='icon'] {
      display: inline-flex !important;
      visibility: visible !important;
      opacity: 1 !important;
      color: #333 !important;
      fill: #333 !important;
    }
  `;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    // Give web component time to render its shadow DOM
    setTimeout(() => this.applyStyles(), 100);
    // Retry after a longer delay
    setTimeout(() => this.applyStyles(), 500);

    // Watch for shadow DOM changes (component might re-render)
    this.setupMutationObserver();
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
  }

  private applyStyles(): void {
    const element = this.el.nativeElement;

    // Find the goa-dropdown inside goab-dropdown (Angular wrapper around web component)
    const goaDropdown = element.querySelector('goa-dropdown');
    const targetElement = goaDropdown || element;

    const shadowRoot = (
      targetElement as HTMLElement & { shadowRoot: ShadowRoot | null }
    ).shadowRoot;

    if (shadowRoot) {
      this.injectStylesIntoShadowRoot(shadowRoot);

      // Style nested goa-icon elements which have their own shadow DOM
      this.styleNestedIcons(shadowRoot);
    }
  }

  /**
   * Style goa-icon elements within the dropdown's shadow DOM
   */
  private styleNestedIcons(parentShadowRoot: ShadowRoot): void {
    const icons = parentShadowRoot.querySelectorAll('goa-icon');

    icons.forEach((icon) => {
      const iconShadowRoot = (
        icon as HTMLElement & { shadowRoot: ShadowRoot | null }
      ).shadowRoot;
      if (iconShadowRoot) {
        this.injectStylesIntoShadowRoot(
          iconShadowRoot,
          this.iconStyles,
          'icon'
        );
      }
    });
  }

  private injectStylesIntoShadowRoot(
    shadowRoot: ShadowRoot,
    styles?: string,
    suffix?: string
  ): void {
    const styleId = suffix
      ? `${DropdownShadowStyleDirective.STYLE_ID}-${suffix}`
      : DropdownShadowStyleDirective.STYLE_ID;

    // Avoid duplicate injection
    if (shadowRoot.querySelector(`#${styleId}`)) {
      return;
    }

    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent =
      styles || this.dropdownShadowStyle || this.defaultStyles;
    shadowRoot.appendChild(styleElement);
  }

  private setupMutationObserver(): void {
    this.mutationObserver = new MutationObserver(() => {
      this.applyStyles();
    });

    this.mutationObserver.observe(this.el.nativeElement, {
      childList: true,
      subtree: true,
    });
  }
}
